
import React, { useState, useEffect } from 'react';
import { Users, Store, Activity, DollarSign, LogOut, Loader2, RefreshCw, ShieldAlert, ChevronRight } from 'lucide-react';
import { db } from '../services/dbService';
import { Shop, RepairJob, Profile, UserRole, Language } from '../types';
import { formatRiel } from '../constants';
// Corrected import path for useTranslation
import { useTranslation } from '../LanguageContext';

interface AdminDashboardProps {
  onLogout: () => void;
  userId: string;
  isSuperAdmin?: boolean;
  onSwitchRole?: () => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout, userId, isSuperAdmin, onSwitchRole }) => {
  const { language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [shops, setShops] = useState<Shop[]>([]);
  const [jobs, setJobs] = useState<RepairJob[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);

  const texts: Record<string, Record<Language, string>> = {
    overview: { EN: 'Platform Overview', KH: 'ទិដ្ឋភាពទូទៅនៃប្រព័ន្ធ', ZH: '平台概览', JA: 'プラットフォーム概要', KO: '플랫폼 개요' },
    master_control: { EN: 'Master Admin Control Panel', KH: 'ផ្ទាំងគ្រប់គ្រងរដ្ឋបាលមេ', ZH: '主管理员控制面板', JA: 'マスター管理コントロールパネル', KO: '마스터 관리 제어판' },
    sign_out: { EN: 'Sign Out', KH: 'ចាកចេញ', ZH: '登出', JA: 'サインアウト', KO: '로그아웃' },
    revenue: { EN: 'Revenue (Est.)', KH: 'ចំណូល (ប៉ាន់ស្មាន)', ZH: '收入 (预估)', JA: '収益 (推定)', KO: '수익 (추정)' },
    vol: { EN: 'Total Volume', KH: 'បរិមាណសរុប', ZH: '总量', JA: '総ボリューム', KO: '총 거래량' },
    shops: { EN: 'Shops', KH: 'បញ្ជីហាង', ZH: '店铺', JA: '店舗', KO: '상점' },
    active_ent: { EN: 'Active Entities', KH: 'អង្គភាពសកម្ម', ZH: '活跃实体', JA: 'アクティブなエンティティ', KO: '활성 엔티티' },
    jobs: { EN: 'Total Jobs', KH: 'ការងារសរុប', ZH: '总作业数', JA: '総ジョブ数', KO: '총 작업 수' },
    requests: { EN: 'Platform Requests', KH: 'សំណើលើប្រព័ន្ធ', ZH: '平台请求', JA: 'プラットフォームリクエスト', KO: '平台请求' },
    users: { EN: 'Registered Users', KH: 'អ្នកប្រើប្រាស់ដែលបានចុះឈ្មោះ', ZH: '注册用户', JA: '登録ユーザー', KO: '등록된 사용자' },
    onboarded: { EN: 'Onboarded', KH: 'ដែលបានចូលរួម', ZH: '已入驻', JA: 'オンボード済み', KO: '온보딩됨' },
    recent_jobs: { EN: 'Recent Jobs', KH: 'ការងារថ្មីៗ', ZH: '近期作业', JA: '最近のジョブ', KO: '최근 작업' },
    live_feed: { EN: 'Live Feed', KH: 'ព័ត៌មានផ្ទាល់', ZH: '实时动态', JA: 'ライブフィード', KO: '라이브 피드' },
    no_jobs: { EN: 'No jobs recorded in the platform database yet.', KH: 'មិនទាន់មានការងារត្រូវបានកត់ត្រានៅក្នុងប្រព័ន្ធនៅឡើយទេ។', ZH: '平台数据库中尚无作业记录。', JA: 'プラットフォームのデータベースにジョブが記録されていません。', KO: '플랫폼 데이터베이스에 아직 기록된 작업이 없습니다.' },
    user_roles: { EN: 'User Roles', KH: 'តួនាទីអ្នកប្រើប្រាស់', ZH: '用户角色', JA: 'ユーザーロール', KO: '사용자 역할' },
    growth: { EN: 'Growth Metric', KH: 'រង្វាស់នៃការរីកចម្រើន', ZH: '增长指标', JA: '成長指標', KO: '성장 지표' },
    active_comm: { EN: 'Active Community', KH: 'សហគមន៍សកម្ម', ZH: '活跃社区', JA: 'アクティブなコミュニティ', KO: '활성 커뮤니티' },
    dynamic_eco: { EN: 'Dynamic repair ecosystem evolving daily.', KH: 'ប្រព័ន្ធអេកូឡូស៊ីជួសជុលដែលមានភាពរស់រវើក និងកំពុងរីកចម្រើនជារៀងរាល់ថ្ងៃ។', ZH: '动态发展的维修生态系统。', JA: '日々進化するダイナミックな修理エコシステム。', KO: '매일 진화하는 역동적인 수리 생태계.' },
    loading_data: { EN: 'Synchronizing Platform Data...', KH: 'កំពុងធ្វើសមកាលកម្មទិន្នន័យប្រព័ន្ធ...', ZH: '正在同步平台数据...', JA: 'プラットフォームデータを同期中...', KO: '플랫폼 데이터 동기화 중...' },
    super_ctrl: { EN: 'Super User Controls', KH: 'ការគ្រប់គ្រងអ្នកប្រើប្រាស់កម្រិតខ្ពស់', ZH: '超级用户控制', JA: '特権ユーザー制御', KO: '슈퍼 유저 컨트롤' },
    switch_desc: { EN: 'Instantly toggle through application roles for debugging and testing.', KH: 'ប្តូរតួនាទីកម្មវិធីភ្លាមៗសម្រាប់ការបំបាត់កំហុស និងការសាកល្បង។', ZH: '立即切换应用角色以进行调试 and 测试。', JA: 'デバッグとテストのためにアプリケーションロールを即座に切り替えます。', KO: '디버깅 및 테스트를 위해 애플리케이션 역할을 즉시 전환합니다.' },
    switch_role: { EN: 'Cycle Application Role', KH: 'ប្តូរតួនាទីប្រើប្រាស់', ZH: '切换应用角色', JA: 'ロールを切り替える', KO: '애플리케이션 역할 순환' }
  };

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [fetchedShops, fetchedJobs, fetchedProfiles] = await Promise.all([
        db.getShops(),
        db.getRepairJobs(userId, UserRole.ADMIN),
        db.getAllProfiles()
      ]);
      setShops(fetchedShops);
      setJobs(fetchedJobs);
      setProfiles(fetchedProfiles);
    } catch (error) {
      console.error("Admin data fetch failed:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalRevenue = jobs.reduce((sum, job) => {
    const costStr = job.estimatedCost?.replace(/[^\d]/g, '') || '0';
    return sum + (parseInt(costStr) || 0);
  }, 0);

  const stats = [
    { label: texts.revenue[language], value: formatRiel(totalRevenue), sub: texts.vol[language], icon: DollarSign, color: 'bg-emerald-500' },
    { label: texts.shops[language], value: shops.length.toString(), sub: texts.active_ent[language], icon: Store, color: 'bg-violet-500' },
    { label: texts.jobs[language], value: jobs.length.toString(), sub: texts.requests[language], icon: Activity, color: 'bg-fuchsia-500' },
    { label: texts.users[language], value: profiles.length.toString(), sub: texts.onboarded[language], icon: Users, color: 'bg-cyan-500' },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-16 h-16 text-violet-600 animate-spin mb-4" />
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{texts.loading_data[language]}</p>
      </div>
    );
  }

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-7xl mx-auto animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-2">{texts.overview[language]}</h1>
          <p className="text-slate-500 font-medium italic">{texts.master_control[language]}</p>
        </div>
        <div className="flex items-center gap-3">
           <button 
            onClick={fetchData} 
            disabled={refreshing}
            className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-violet-600 transition-all shadow-sm active:scale-95"
           >
             <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
           </button>
           <button 
            onClick={onLogout}
            className="flex items-center gap-2 px-6 py-4 bg-red-50 text-red-600 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-red-100 transition-all shadow-sm active:scale-95"
           >
             <LogOut size={16} /> {texts.sign_out[language]}
           </button>
        </div>
      </div>

      {isSuperAdmin && (
        <section className="mb-12 bg-slate-900 rounded-[3rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-12 opacity-10 group-hover:scale-110 transition-transform duration-700">
            <ShieldAlert size={120} />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-violet-600/30 px-4 py-2 rounded-2xl border border-violet-500/30 mb-4">
                 <ShieldAlert size={16} className="text-violet-400" />
                 <span className="text-[10px] font-black uppercase tracking-[0.2em] text-violet-100">{texts.super_ctrl[language]}</span>
              </div>
              <h2 className="text-3xl font-black mb-2 tracking-tight">Super Admin Privileges</h2>
              <p className="text-slate-400 font-bold max-w-md">{texts.switch_desc[language]}</p>
            </div>
            <button 
              onClick={onSwitchRole}
              className="px-10 py-6 bg-white text-slate-900 rounded-[2rem] font-black text-xs uppercase tracking-widest hover:bg-violet-600 hover:text-white transition-all shadow-2xl flex items-center gap-4 transform active:scale-95 group/btn"
            >
              <RefreshCw size={20} className="group-hover/btn:rotate-180 transition-transform duration-500" /> 
              {texts.switch_role[language]}
              <ChevronRight size={18} className="opacity-40" />
            </button>
          </div>
        </section>
      )}

      {/* Real-time Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {stats.map((stat, idx) => (
          <div key={idx} className="relative overflow-hidden bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm group hover:shadow-xl transition-all duration-500">
             <div className="relative z-10">
               <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-6 ${stat.color} shadow-lg shadow-current/20`}>
                 <stat.icon size={24} />
               </div>
               <h3 className="text-3xl font-black text-slate-900 mb-1 tracking-tighter">{stat.value}</h3>
               <div className="flex justify-between items-center">
                 <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">{stat.label}</span>
                 <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100">{stat.sub}</span>
               </div>
             </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Recent Platform Activity */}
        <div className="lg:col-span-2 bg-white rounded-[3rem] shadow-xl shadow-slate-100 border border-slate-50 p-8 md:p-10">
          <div className="flex justify-between items-center mb-10">
             <div>
               <h3 className="text-2xl font-black text-slate-900 tracking-tight">{texts.recent_jobs[language]}</h3>
               <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">{texts.live_feed[language]}</p>
             </div>
          </div>
          
          <div className="space-y-4">
             {jobs.length === 0 ? (
               <div className="text-center py-20 text-slate-300 font-bold">{texts.no_jobs[language]}</div>
             ) : (
               jobs.slice(0, 10).map((job) => (
                 <div key={job.id} className="p-5 bg-slate-50 rounded-[2rem] border border-slate-100 flex items-center justify-between group hover:bg-white hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center gap-4">
                       <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white text-xs font-black">
                          {job.shopName.charAt(0)}
                       </div>
                       <div>
                          <p className="text-sm font-black text-slate-900">{job.shopName}</p>
                          <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">{job.status.replace('_', ' ')}</p>
                       </div>
                    </div>
                    <div className="text-right">
                       <p className="text-sm font-black text-slate-900">{job.estimatedCost || '---'}</p>
                       <p className="text-[9px] text-slate-300 font-black uppercase">{new Date(job.createdAt).toLocaleDateString()}</p>
                    </div>
                 </div>
               ))
             )}
          </div>
        </div>

        {/* User Distribution */}
        <div className="bg-white rounded-[3rem] shadow-xl shadow-slate-100 border border-slate-50 p-8 md:p-10">
          <h3 className="text-2xl font-black text-slate-900 tracking-tight mb-10">{texts.user_roles[language]}</h3>
          <div className="space-y-6">
            {[UserRole.CUSTOMER, UserRole.SHOP_OWNER, UserRole.STAFF, UserRole.ADMIN].map((role) => {
              const count = profiles.filter(p => p.role === role).length;
              const percentage = profiles.length > 0 ? (count / profiles.length) * 100 : 0;
              return (
                <div key={role} className="space-y-2">
                   <div className="flex justify-between items-center">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{role}</span>
                      <span className="text-xs font-black text-slate-900">{count}</span>
                   </div>
                   <div className="h-3 bg-slate-50 rounded-full overflow-hidden shadow-inner">
                      <div 
                        className="h-full bg-violet-600 rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(124,58,237,0.4)]"
                        style={{ width: `${percentage}%` }}
                      />
                   </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 p-8 bg-slate-900 rounded-[2.5rem] text-white relative overflow-hidden group">
             <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform duration-700"><Activity size={64} /></div>
             <p className="text-[9px] font-black text-violet-400 uppercase tracking-[0.3em] mb-4">{texts.growth[language]}</p>
             <p className="text-xl font-black tracking-tight mb-2">{texts.active_comm[language]}</p>
             <p className="text-slate-400 text-xs font-bold italic leading-relaxed opacity-80">{texts.dynamic_eco[language]}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
